import React from "react";
import styled, { css } from "styled-components";
import { useCombobox } from "downshift";
import { toast } from "react-toastify";

import { Text } from "src/library/components/Text/Text";
import {
  IDashboard,
  useDashboardApi
} from "src/core/dashboard/api/dashboard.api";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { useOutsideItemClick } from "src/library/hooks";
import { Button } from "src/library/components/Button";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";

import { DeleteDashboardModel } from "./DeleteDashboardModel";

export interface DashboardSwitcherOptionsProps {
  open: boolean;
  selectedDashboard?: IDashboard | null;
  availableDashboards: IDashboard[];
  getDashboardsLoading: boolean;
  totalDashboards?: number;
  fetchDashboards: (searchValue: string, clearCache?: boolean) => void;
  onDismiss: () => void;
  onSelectDashboard: (dashboard: IDashboard) => void;
  onCreateDashboard: () => void;
}

/**
 * Renders a list of dashboard options with fuzzy search functionality to find the dashboard the user desires
 */
const DashboardSwitcherOptions: React.FC<DashboardSwitcherOptionsProps> = ({
  open,
  selectedDashboard,
  availableDashboards,
  getDashboardsLoading,
  totalDashboards,
  fetchDashboards,
  onDismiss,
  onSelectDashboard,
  onCreateDashboard
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const optionsRef = React.useRef<HTMLDivElement>(null);
  const [ deleteDashboardRes, deleteDashboardReq ] = useDashboardApi("DELETE_DASHBOARD");
  const [ dashboardToDelete, setDashboardToDelete ] = React.useState<IDashboard | null>(null);

  const downshiftProps = useCombobox<IDashboard>({
    items: availableDashboards || [],
    isOpen: true,
    onInputValueChange: ({ inputValue }) => {
      fetchDashboards(inputValue || "");
    },
    itemToString: () => "",
    onSelectedItemChange: selectedDashboard => {
      onDismiss();

      if (selectedDashboard.selectedItem?.id) {
        onSelectDashboard(selectedDashboard.selectedItem);
      }
    }
  });

  const inputProps = downshiftProps.getInputProps({
    className: "dashboard-search-input",
    id: "dashboard-search",
    placeholder: "Filter dashboards",
    min: 0,
    suppressRefError: true
  }, { suppressRefError: true });

  const menuProps = downshiftProps.getMenuProps({ suppressRefError: true }, { suppressRefError: true });

  // when clicking outside component, run onDismiss callback
  useOutsideItemClick(optionsRef, () => onDismiss());

  // handle deleting a dashboard, verify that the dashboard is not currently selected
  const handleDeleteDashboard = React.useCallback(() => {
    if (!dashboardToDelete?.id) {
      toast.error("Cannot find dashboard to delete");

      return;
    }

    deleteDashboardReq({ id: dashboardToDelete?.id });
  }, [ dashboardToDelete, deleteDashboardReq ]);

  React.useEffect(() => {
    if (deleteDashboardRes.error) {
      toast.error(deleteDashboardRes.error.message);
      setDashboardToDelete(null);
    }

    // only run if dashboard is set and we have a successful response
    if (deleteDashboardRes.data && dashboardToDelete !== null) {
      onDismiss();
      // refetch dashboard to ensure updated list
      fetchDashboards("", true);
      toast.success("Dashboard deleted successfully");
      deleteDashboardRes.data = null;
      setDashboardToDelete(null);
    }
  }, [
    dashboardToDelete,
    deleteDashboardRes,
    deleteDashboardRes.data,
    deleteDashboardRes.error,
    fetchDashboards,
    onDismiss
  ]);

  // if open is false, just return the model
  if (!open) {
    return (
      <DeleteDashboardModel
        open={dashboardToDelete !== null}
        onDismiss={() => setDashboardToDelete(null)}
        onConfirm={handleDeleteDashboard}
        dashboardName={dashboardToDelete?.name}
        isLoading={deleteDashboardRes.pending}
      />
    );
  }

  return (
    <StyledWrapper ref={optionsRef}>
      {!!totalDashboards && (
        <div className="switcher-header">
          <Text
            color="secondary"
            fontSize="sub"
          >
            {`${totalDashboards} dashboards available`}
          </Text>
        </div>
      )}

      <div>
        <input
          ref= {inputRef}
          {...inputProps}
        />
      </div>

      <div className="search-results-wrapper">
        {getDashboardsLoading && (
          <div className="options-loading">
            <LoadingSpinner
              type="flat"
              position="inline"
              size={5}
            />
          </div>
        )}

        <ul
          className="search-results"
          {...menuProps}
        >

          {!getDashboardsLoading && availableDashboards.length === 0 ? (
            <div className="no-results">
              <Text>
                No dashboards found that contain your search criteria. Please try again.
              </Text>
            </div>
          ) : (
            availableDashboards.map((item, index) => (
              <div
                className={`dashboard-item-wrapper ${item.id === selectedDashboard?.id && "selected"}`}
                key={item.id}
              >
                <div
                  className={`dashboard-item ${downshiftProps.highlightedIndex === index && "highlighted"}`}
                  {...downshiftProps.getItemProps({
                    item,
                    index,
                    suppressRefError: true
                  })}
                >
                  <div className="name">
                    <Text>
                      {item.name}
                    </Text>
                  </div>
                </div>

                <div className="dashboard-item-actions">
                  {item.default ? (
                    <Text>
                      DEFAULT
                    </Text>
                  ) : deleteDashboardRes.pending && dashboardToDelete?.id === item.id ? (
                    <LoadingSpinner
                      type="circular"
                      position="inline"
                      size={20}
                    />
                  ) : (
                    <Icon
                      icon={Icons.DELETE}
                      size={20}
                      $fillColor="midGrey"
                      onClick={() => {
                        if (selectedDashboard?.id === item.id) {
                          toast.error("Cannot delete currently selected dashboard");

                          return;
                        }

                        setDashboardToDelete(item);
                        onDismiss();
                      }}
                    />
                  )}
                </div>
              </div>
            ))
          )}
        </ul>

        <div className="create-dashboard-button-wrapper">
          <Button
            className="create-dashboard-button"
            onClick={onCreateDashboard}
          >
            Create New
          </Button>
        </div>
      </div>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.white};
    width: 300px;
    position: absolute;
    z-index: 2;
    border: 1px solid ${theme.colors.lightGrey};
    box-shadow: ${theme.shadows.default};

    .switcher-header {
      padding: ${theme.spacing.padding.sm};
      display: block;
      width: 100%;
    }

    .dashboard-search-input {
      height: 40px;
      padding: 10px 15px;
      outline: none;
      border: none;
      width: 100%;
      border-top: 1px solid ${theme.colors.lightGrey};
      border-bottom: 1px solid ${theme.colors.lightGrey};
    }

    .options-loading {
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .no-results {
      padding:  ${theme.spacing.padding.sm};
    }

    .dashboard-item-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &.highlighted, &.selected {
        background: ${theme.colors.lightestGrey};

        .name span {
          font-weight: ${theme.fonts.weights.bold};
        }
      }
    }

    .dashboard-item-wrapper:hover {
      background: ${theme.colors.lightGrey};
    }

    .dashboard-item-wrapper:not(:last-child) {
      border-bottom: 1px solid ${theme.colors.lightGrey};
    }

    .dashboard-item {
      padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.md};
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      
      &:hover {
        cursor: pointer;
      } 

      &.highlighted {
        background: ${theme.colors.lightGrey};
      }
    }

    .dashboard-item-actions {
      font-style: italic;
      padding-right: ${theme.spacing.padding.sm};

      svg:hover {
        cursor: pointer;
        fill: ${theme.colors.error} !important;
      }
    }

    .create-dashboard-button-wrapper {
      display: flex;
      justify-content: flex-end;
      padding: ${theme.spacing.padding.xs};
    }

    .create-dashboard-button {
      background-color: ${theme.colors.primaryDark};
    }
  `}
`;

export default DashboardSwitcherOptions;
